import { useMemo, useState } from 'react';
import { Search, ClipboardList, Loader2 } from 'lucide-react';

import { useWaitlistQuery } from '@/client/api/admin/marketing.query';
import type {
  WaitlistListParams,
  WaitlistSortField,
} from '@/client/types/admin/marketing';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import useDebounce from '@/client/hooks/useDebounce';
import SortableTh from '@/client/components/common/SortableTh';
import Pagination from '@/client/components/common/Pagination';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString() : '—';

const WaitlistPage = () => {
  useBreadcrumbs([
    { label: 'Users Management', href: '#' },
    { label: 'Waitlist' },
  ]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [sortBy, setSortBy] = useState<WaitlistSortField>('date_added');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const params = useMemo<WaitlistListParams>(() => {
    const next: WaitlistListParams = {
      page,
      page_size: limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (debouncedSearch) next.search = debouncedSearch;
    return next;
  }, [page, limit, sortBy, sortOrder, debouncedSearch]);

  const { data, isLoading, isFetching } = useWaitlistQuery(params);
  const users = data?.users ?? [];
  const total = data?.total ?? 0;

  const toggleSort = (field: string) => {
    const nextOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field as WaitlistSortField);
    setSortOrder(nextOrder);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <ClipboardList className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Waitlist</h1>
            <p className="text-sm text-gray-500">
              Leads captured through marketing campaigns
              {total ? ` · ${total.toLocaleString()} total` : ''}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search name, email, phone, interest, campaign…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh label="Name" field="full_name" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                  <SortableTh label="Email" field="email" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                  <SortableTh label="Phone" field="phone_number" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                  <SortableTh label="Interest" field="interest" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                  <SortableTh label="Campaign" field="campaign_type" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                  <SortableTh label="Joined" field="date_added" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading &&
                  Array.from({ length: limit }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-400">
                      {debouncedSearch
                        ? 'No waitlist users match your search.'
                        : 'No waitlist users yet.'}
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  users.map((u, i) => (
                    <tr key={(u.email as string) ?? i} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {u.full_name || '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {u.email || '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {u.phone_number || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.interest ? (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                            {u.interest}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {u.campaign_type || '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-500">
                        {fmtDate(u.date_added)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-200 px-4 py-3">
            <Pagination
              total={total}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={setPage}
              setItemsPerPage={(n) => {
                setLimit(n);
                setPage(1);
              }}
              showButtons
              showSelect
              selectOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;
